/**
 * The period filters and the budget selector above every page. The period (year, months, as-of)
 * comes from PeriodFilters; the budget dropdown only appears when more than one active Sage budget
 * covers the period (budgets Sage marks inactive are never offered). An info popover explains how
 * FPA picks the budget. Admins (`canConfigureAccounting`) can pin the shown budget as FPA's
 * default — after confirming what that does — or go back to following Sage. Selections live in the
 * URL (`?y=&m=&asof=`, `?budget=`).
 */
import { type ChangeEvent, useCallback, useMemo, useState } from 'react'

import { errorMessage } from '@/api/client'
import type { BudgetOption, Runway, RunwayBudget } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { useMe } from '@/features/auth/MeContext'
import { budgetSecondary, budgetText } from '@/lib/budgetLabel'

import { useBudgetsQuery, usePinBudget } from '../hooks/useBudgets'
import { usePeriodActions, usePeriodSelection } from '../hooks/usePeriod'
import { usePeriodsQuery } from '../hooks/useRunway'
import { BudgetReleaseNotice } from './BudgetReleaseNotice'
import styles from './PeriodBar.module.css'
import { PeriodFilters } from './PeriodFilters'

const SOURCE_LABEL: Record<RunwayBudget['source'], string> = {
  requested: 'Selected',
  pinned: 'Pinned for FPA',
  sage_default: 'Sage default',
  latest: 'Most recent in Sage',
}

/** The Sage budget ID: the name finance knows (descriptions are often date stamps). */
function budgetName(budget: Pick<RunwayBudget, 'id'>): string {
  return budget.id
}

function optionLabel(option: BudgetOption): string {
  const tags = [option.isDefault ? 'Sage default' : null, option.pinned ? 'pinned' : null].filter(Boolean)
  return tags.length ? `${budgetText(option)} (${tags.join(', ')})` : budgetText(option)
}

function BudgetRulesHelp({
  canPin,
  inactiveCount,
  sageDefault,
}: {
  canPin: boolean
  inactiveCount: number
  sageDefault: BudgetOption | null
}) {
  return (
    <InfoPopover label="How FPA picks the budget">
      <p>For each quarter, FPA compares spend against the first budget that applies:</p>
      <ol>
        <li>
          <b>Pinned for FPA</b> — a budget an FPA admin pinned, for every quarter it covers.
        </li>
        <li>
          <b>Sage default</b> — the budget Sage marks as default
          {sageDefault ? ` (now: ${budgetName(sageDefault)})` : ''}.
        </li>
        <li>
          <b>Most recent in Sage</b> — otherwise, the most recently changed active budget.
        </li>
      </ol>
      <p>
        <b>Pinning</b> makes a budget everyone&rsquo;s default in FPA. When Sage later makes a different budget its
        default, FPA releases the pin automatically and follows Sage again.
      </p>
      <p>
        Choosing another budget in the list changes only your view (it stays in the link you share), not FPA&rsquo;s
        default.
        {inactiveCount > 0
          ? ` ${inactiveCount} ${inactiveCount === 1 ? 'budget is' : 'budgets are'} inactive in Sage and not offered.`
          : ' Budgets inactive in Sage are never used.'}
      </p>
      <p className={styles.helpWho}>
        {canPin
          ? 'You can pin a budget (FPA admin).'
          : 'Only FPA admins can pin a budget. Ask an FPA admin if the default should change.'}
      </p>
    </InfoPopover>
  )
}

export function PeriodBar({ runway }: { runway: Runway }) {
  const me = useMe()
  const periods = usePeriodsQuery()
  const { budgetId: _budgetId, ...selection } = usePeriodSelection()
  const budgets = useBudgetsQuery(selection)
  const pin = usePinBudget()
  const { setBudget } = usePeriodActions()
  const [confirm, setConfirm] = useState<'pin' | 'unpin' | null>(null)

  const all = useMemo(() => budgets.data ?? [], [budgets.data])
  const shown = runway.budget
  // Inactive budgets are left out, except the one on screen (an explicit `?budget=` link).
  const options = useMemo(() => all.filter((o) => o.active || o.id === shown?.id), [all, shown?.id])
  const inactiveCount = all.filter((o) => !o.active).length
  const resolvedId = all.find((o) => o.resolved)?.id ?? null
  const pinned = all.find((o) => o.pinned) ?? null
  const sageDefault = all.find((o) => o.isDefault && o.active) ?? null
  const shownOption = all.find((o) => o.id === shown?.id) ?? null
  const shownInactive = shownOption !== null && !shownOption.active

  const handleBudget = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const id = event.target.value
      setBudget(id === resolvedId ? null : id)
    },
    [setBudget, resolvedId],
  )
  const { mutate: mutatePin, reset: resetPin } = pin
  const askPin = useCallback(() => setConfirm('pin'), [])
  const askUnpin = useCallback(() => setConfirm('unpin'), [])
  const cancel = useCallback(() => {
    resetPin()
    setConfirm(null)
  }, [resetPin])
  const confirmPin = useCallback(() => {
    if (!shown) return
    mutatePin(shown.id, {
      onSuccess: () => {
        setConfirm(null)
        setBudget(null)
      },
    })
  }, [mutatePin, shown, setBudget])
  const confirmUnpin = useCallback(() => {
    mutatePin(null, {
      onSuccess: () => {
        setConfirm(null)
        setBudget(null)
      },
    })
  }, [mutatePin, setBudget])

  const canPin = me.permissions.canConfigureAccounting
  const showPin = canPin && shown !== null && !shownInactive && !(shown.source === 'pinned' || pinned?.id === shown.id)
  const showUnpin = canPin && pinned !== null
  const pinError = pin.isError ? errorMessage(pin.error, 'Could not change the FPA budget.') : null

  return (
    <>
      <div role="group" aria-label="Period and budget">
        <PeriodFilters period={runway.period} periods={periods.data} />
        <div className={styles.bar} role="group" aria-label="Budget">
          {runway.period.complete ? (
            <Badge tone="neutral" small>
              Closed period
            </Badge>
          ) : null}
          {runway.period.complete ? <span className={styles.divider} aria-hidden="true" /> : null}

          {shown === null ? (
            <span className={styles.budget}>
              <b>No budget on file</b> <span className={styles.muted}>for {runway.period.label} in Sage</span>
            </span>
          ) : options.length > 1 ? (
            <label className={styles.field}>
              <span className={styles.label}>Budget</span>
              <select className={styles.select} value={shown.id} onChange={handleBudget} aria-label="Budget">
                {options
                  .filter((o) => o.active)
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {optionLabel(o)}
                    </option>
                  ))}
                {shownInactive && shownOption ? (
                  <optgroup label="Inactive in Sage">
                    <option value={shownOption.id}>{budgetText(shownOption)}</option>
                  </optgroup>
                ) : null}
              </select>
            </label>
          ) : (
            <span className={styles.budget}>
              Budget: <b>{budgetName(shown)}</b>
              {budgetSecondary(shown) ? <span className={styles.muted}> · {budgetSecondary(shown)}</span> : null}
            </span>
          )}
          {shown ? <span className={styles.muted}>{SOURCE_LABEL[shown.source]}</span> : null}
          {shownInactive ? (
            <Badge tone="warn" small>
              Inactive in Sage
            </Badge>
          ) : null}
          <BudgetRulesHelp canPin={canPin} inactiveCount={inactiveCount} sageDefault={sageDefault} />

          {showPin || showUnpin ? (
            <span className={styles.actions}>
              {showPin ? (
                <button type="button" className={styles.action} onClick={askPin} disabled={pin.isPending}>
                  <ICONS.pin size={13} />
                  Set as default for FPA
                </button>
              ) : null}
              {showUnpin ? (
                <button type="button" className={styles.action} onClick={askUnpin} disabled={pin.isPending}>
                  <ICONS.refresh size={13} />
                  Follow Sage default
                </button>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>

      <BudgetReleaseNotice release={runway.budget_pin_release} />

      {confirm === 'pin' && shown ? (
        <ConfirmDialog
          title="Pin this budget for everyone?"
          confirmLabel="Pin budget"
          busy={pin.isPending}
          error={pinError}
          onConfirm={confirmPin}
          onCancel={cancel}
        >
          <p>
            Everyone in FPA will compare spend against <b>{budgetName(shown)}</b> for every quarter it covers, instead
            of {sageDefault ? <b>{budgetName(sageDefault)}</b> : 'the budget Sage picks'}.
          </p>
          <p>
            If Sage later makes a different budget its default, FPA releases this pin automatically and follows Sage.
            The change is recorded with your name.
          </p>
        </ConfirmDialog>
      ) : null}
      {confirm === 'unpin' && pinned ? (
        <ConfirmDialog
          title="Follow Sage's default again?"
          confirmLabel="Remove pin"
          tone="danger"
          busy={pin.isPending}
          error={pinError}
          onConfirm={confirmUnpin}
          onCancel={cancel}
        >
          <p>
            FPA stops using <b>{budgetName(pinned)}</b> and follows Sage:{' '}
            {sageDefault ? (
              <>
                <b>{budgetName(sageDefault)}</b> (Sage&rsquo;s default)
              </>
            ) : (
              'its default budget, or the most recently changed one'
            )}{' '}
            for every quarter, for everyone.
          </p>
        </ConfirmDialog>
      ) : null}
    </>
  )
}
